import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { technicalAuditService } from "@/services/technicalAuditService";
import type { FixHistoryEntry } from "@/types/technicalAudit";

// A4: chronological "who reviewed/applied/skipped, when, what changed" timeline
// for a single issue. Read-only; data comes from the fix-history audit log.

const ACTION_LABEL: Record<string, string> = {
	approve: "Suggestion saved",
	reject: "Skipped",
	skip: "Skipped",
	apply: "Applied",
	rollback: "Rolled back",
	verify: "Verified",
};

const STATUS_LABEL: Record<string, string> = {
	pending: "Pending",
	approved: "Suggested",
	rejected: "Skipped",
	applied: "Applied",
	verified: "Verified",
	failed: "Failed",
};

function formatWhen(value: string): string {
	const ts = new Date(value);
	return Number.isNaN(ts.getTime()) ? value : ts.toLocaleString();
}

// A review-only decline is logged as an "apply" that ends not-applied (status
// reverts to pending) with a review_only diff — it's the §2.5/§2.6 safeguard, not
// a success or a failure.
function isReviewOnlyApply(entry: FixHistoryEntry): boolean {
	if (entry.action !== "apply") return false;
	const diff = (entry as { diff?: { review_only?: boolean } }).diff;
	if (diff?.review_only) return true;
	return (
		entry.new_status != null &&
		entry.new_status !== "applied" &&
		entry.new_status !== "failed"
	);
}

function actionLabel(entry: FixHistoryEntry): string {
	if (isReviewOnlyApply(entry)) return "Reviewed — not applied";
	if (entry.action === "apply" && entry.new_status === "failed") return "Apply failed";
	return ACTION_LABEL[entry.action] ?? entry.action;
}

function statusLabel(status?: string | null): string {
	if (!status) return "—";
	return STATUS_LABEL[status] ?? status.replace(/_/g, " ");
}

export function FixHistoryTimeline({ issueId }: { issueId: string }) {
	const { data, isLoading, isError } = useQuery({
		queryKey: ["technical-audit-issue-history", issueId],
		queryFn: () => technicalAuditService.getIssueHistory(issueId),
		enabled: Boolean(issueId),
	});

	const entries = data ?? [];

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Fix history</CardTitle>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading history…</p>
				) : isError ? (
					<p className="text-sm text-muted-foreground">Could not load history.</p>
				) : entries.length === 0 ? (
					<p className="text-sm text-muted-foreground">No history yet.</p>
				) : (
					<ol className="space-y-3">
						{entries.map((entry) => (
							<li
								key={entry.id}
								className="border-l-2 border-muted pl-3 text-sm"
							>
								<div className="flex flex-wrap items-center gap-x-2">
									<span className="font-medium">{actionLabel(entry)}</span>
									{entry.actor_email && (
										<span className="text-muted-foreground">
											by {entry.actor_email}
										</span>
									)}
									<span className="text-xs text-muted-foreground">
										{formatWhen(entry.created_at)}
									</span>
								</div>
									{(entry.old_status || entry.new_status) && (
										<div className="text-xs text-muted-foreground">
											{statusLabel(entry.old_status)} → {statusLabel(entry.new_status)}
										</div>
									)}
								{entry.error && (
									<div
										className={
											isReviewOnlyApply(entry)
												? "text-xs text-muted-foreground"
												: "text-xs text-destructive"
										}
									>
										{entry.error}
									</div>
								)}
							</li>
						))}
					</ol>
				)}
			</CardContent>
		</Card>
	);
}
